import {
    Directive,
    Input,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewContainerRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { PermissionService } from './permission.service';
import { PermissionStrategy } from './permission.types';

/**
 * Structural Directive untuk menampilkan/menyembunyikan elemen DOM
 * berdasarkan hak akses permission pengguna.
 *
 * Contoh penggunaan:
 * 1. Single permission:
 *    <button *appHasPermission="'product.create'">Tambah</button>
 *
 * 2. Multiple permissions (Default ANY - diizinkan jika memiliki salah satu):
 *    <button *appHasPermission="['product.create', 'product.update']">Kelola</button>
 *
 * 3. Multiple permissions dengan Strategi ALL (harus memiliki seluruhnya):
 *    <button *appHasPermission="['product.read', 'product.export']; strategy: 'ALL'">Export</button>
 *
 * 4. Fallback Template (Else):
 *    <button *appHasPermission="'product.delete'; else noAccess">Hapus</button>
 *    <ng-template #noAccess><span>Akses dibatasi</span></ng-template>
 */
@Directive({
    selector: '[appHasPermission]',
    standalone: true,
})
export class HasPermissionDirective implements OnInit, OnDestroy {
    private _permission: string | string[] | null | undefined = null;
    private _strategy: PermissionStrategy = 'ANY';
    private _elseTemplateRef: TemplateRef<any> | null = null;
    private _hasView = false;
    private _sub!: Subscription;

    constructor(
        private _templateRef: TemplateRef<any>,
        private _viewContainer: ViewContainerRef,
        private _permissionService: PermissionService
    ) {}

    @Input('appHasPermission')
    set appHasPermission(val: string | string[] | null | undefined) {
        this._permission = val;
        this._updateView();
    }

    @Input('appHasPermissionStrategy')
    set appHasPermissionStrategy(strategy: PermissionStrategy | null | undefined) {
        this._strategy = strategy || 'ANY';
        this._updateView();
    }

    @Input('appHasPermissionElse')
    set appHasPermissionElse(templateRef: TemplateRef<any> | null) {
        this._elseTemplateRef = templateRef;
        this._updateView();
    }

    ngOnInit(): void {
        // Reaktif terhadap perubahan permission (misal setelah login/logout/refresh data user)
        this._sub = this._permissionService.permissions$.subscribe(() => {
            this._updateView();
        });
    }

    ngOnDestroy(): void {
        if (this._sub) {
            this._sub.unsubscribe();
        }
    }

    /**
     * Evaluasi hak akses dan manipulasi ViewContainer
     */
    private _updateView(): void {
        const hasAccess = this._permissionService.hasAccess(
            this._permission,
            this._strategy
        );

        if (hasAccess) {
            if (!this._hasView) {
                this._viewContainer.clear();
                this._viewContainer.createEmbeddedView(this._templateRef);
                this._hasView = true;
            }
        } else {
            this._viewContainer.clear();
            this._hasView = false;

            // Render fallback template jika disediakan
            if (this._elseTemplateRef) {
                this._viewContainer.createEmbeddedView(this._elseTemplateRef);
            }
        }
    }
}
